/**
 * ApprovedProjectsSection - Shared component for displaying approved projects
 */

import { ApprovedProject } from '@/types/tenant.types';

interface ApprovedProjectsSectionProps {
  projects: ApprovedProject[];
  primaryColor: string;
  variant?: 'default' | 'cards' | 'timeline';
}

export function ApprovedProjectsSection({
  projects,
  primaryColor,
  variant = 'default',
}: ApprovedProjectsSectionProps) {
  if (variant === 'timeline') {
    return (
      <div className="relative pl-8">
        <div
          className="absolute left-3 top-0 bottom-0 w-0.5"
          style={{ backgroundColor: primaryColor }}
        />
        {projects.map((project, i) => (
          <div key={i} className="relative pb-6 last:pb-0">
            <div
              className="absolute left-[-22px] w-4 h-4 rounded-full bg-white border-2"
              style={{ borderColor: primaryColor }}
            />
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <p className="font-semibold text-gray-900">{project.name}</p>
              <p className="text-sm text-gray-500">
                {project.mandate} - {project.year}
              </p>
              {project.description && (
                <p className="text-sm text-gray-600 mt-2">{project.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div className="grid md:grid-cols-2 gap-4">
        {projects.map((project, i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-5 shadow-sm border-l-4"
            style={{ borderColor: primaryColor }}
          >
            <div className="flex items-start gap-3">
              <div
                className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <svg
                  className="w-5 h-5"
                  style={{ color: primaryColor }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">{project.name}</p>
                <p className="text-sm text-gray-500">
                  {project.mandate} - {project.year}
                </p>
                {project.description && (
                  <p className="text-sm text-gray-600 mt-2">{project.description}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default: simple list
  return (
    <ul className="space-y-4">
      {projects.map((project, i) => (
        <li key={i} className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-white"
            style={{ backgroundColor: primaryColor }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900">{project.name}</p>
            <p className="text-sm text-gray-500">
              {project.mandate} - {project.year}
            </p>
            {project.description && (
              <p className="text-sm text-gray-600 mt-1">{project.description}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
